import React from 'react';
import { cn } from '@/lib/utils';
import { emsTabClass } from '@/components/ui/ems-tabs';
import { Badge } from '@/components/ui/badge';

export interface ViewOption<T extends string = string> {
  id: T;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: number | string;
  badgeClassName?: string;
}

export interface SegmentedViewSwitcherProps<T extends string = string> {
  options: ViewOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  size?: 'sm' | 'default';
  ariaLabel?: string;
}

export function SegmentedViewSwitcher<T extends string = string>({
  options,
  value,
  onChange,
  className,
  size = 'default',
  ariaLabel = 'Chế độ xem',
}: SegmentedViewSwitcherProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        // Tab chuẩn EMS (thẻ + gạch chân đậm khi chọn), cỡ gọn của thanh công cụ danh sách
        "flex items-end gap-1 select-none shrink-0 overflow-x-auto no-scrollbar",
        className
      )}
    >
      {options.map((option) => {
        const isActive = value === option.id;
        const Icon = option.icon;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(option.id)}
            className={cn(emsTabClass(isActive, { size: "sm" }), "outline-none", size === 'sm' && "h-6 px-2 text-[11px]")}
          >
            {Icon && (
              <Icon
                className={cn(
                  "shrink-0",
                  size === 'sm' ? "w-3 h-3" : "w-3.5 h-3.5",
                  isActive ? "text-primary" : "text-slate-500"
                )}
              />
            )}
            <span className={cn(options.length > 3 ? "hidden sm:inline" : "inline")}>
              {option.label}
            </span>
            {option.badge !== undefined && option.badge !== null && (
              <span
                className={cn(
                  "inline-flex items-center justify-center font-mono text-[9px] font-bold rounded-full px-1.5 h-4 min-w-4",
                  option.badgeClassName ||
                    (isActive
                      ? "bg-primary/10 text-primary"
                      : "bg-slate-200 text-slate-600")
                )}
              >
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
